import { Button, DropdownMenu, TableItem, Timeline } from 'components/atoms';
import { FormInput, MagellanPopups } from 'components/molecules';
import SendButtonEmail from 'components/molecules/buttons/sendEmail/SendButtonEmail';
import LabeledEmailSend from 'components/molecules/labeledStatus/LabeledEmailSend';
import LabeledSettlementOrderStatus from 'components/molecules/labeledStatus/LabeledSettlementOrderStatus';
import { FooterTable, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useFormikContext } from 'formik';
import { useComponentVisible, useHasPermission } from 'hooks';
import { useInfiniteTable } from 'hooks/useInfiniteTable';
import { useCustomParamId } from 'hooks/useParamId';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useStreamFileDownload } from 'hooks/useStreamFileDownload';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { settlementOrderByLabelValidator } from 'pages/private/settlement-order/list/Settlement-order';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiCheck, PiDownload, PiX } from 'react-icons/pi';
import { PopupActions } from 'reactjs-popup/dist/types';
import { useQueryClient } from '@tanstack/react-query';
import { DOCUMENT_TYPE } from 'services/email/types';
import { useCheckSettlementPeriods } from 'services/labels';
import { ILabelForm } from 'services/labels/types';
import {
  useDeleteSettlement,
  useGetSettlementFile,
  useGetSettlementFilePreview,
  useGetSettlementLabelInfinite,
  useGetSettlementLabelStatistics,
  useGetSettlementOrderQueue,
  useGetSettlementTimeline,
} from 'services/settlement';
import { IGetSettlementLabelInfiniteParams } from 'services/settlement/types';
import {
  mapCreateSettlementOrderBody,
  mapCreateSettlementOrderToNewestBody,
} from 'services/settlementOrder/settlementOrder.utils';
import {
  useAcceptSettlementOrder,
  useCreateSettlementOrder,
  useCreateSettlementOrderToNewest,
  useRejectSettlementOrder,
} from 'services/settlementOrder';
import { SETTLEMENT_ORDER_STATUS } from 'services/settlementOrder/enums';
import { useGetSettlementLightList } from 'services/settlementPeriods';
import { EMAIL_SEND } from 'services/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatCurrency } from 'utils/global/currency.utils';
import { formatDate, formatDateToInput } from 'utils/global/date.utils';
import { getSettlementPeriodName } from 'utils/global/name.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import { object } from 'yup';

enum SORT_COLUMNS {
  number,
  month,
  year,
  startDate,
  endDate,
  net,
  settlementNumber,
}

export const settlementDetailValidator = object().shape({
  generate: settlementOrderByLabelValidator,
});

const Settlements = () => {
  const labelId = useCustomParamId('id', '/label');
  const hasPermission = useHasPermission();

  usePermissionGuard('SettlementOrderPermissionsLIST**');

  const { values, setFieldValue, initialValues } =
    useFormikContext<ILabelForm>();

  const settlementTimeline = useGetSettlementTimeline(labelId);

  const sortedTimeline = useMemo(
    () =>
      settlementTimeline.data?.sort(
        (a, b) =>
          new Date(a.startDate).getTime() - new Date(b.endDate).getTime()
      ),
    [settlementTimeline.data]
  );

  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { setFile } = useStreamFileDownload(useGetSettlementFile);

  const columnsVisibility = useTableColumnsVisibility(
    'LabelSettlementsEmail2',
    SORT_COLUMNS
  );

  const setlementSearch: IGetSettlementLabelInfiniteParams = {
    StartSettlementPeriodId:
      values.settlement?.search?.startSettlementPeriodId ?? '',
    EndSettlementPeriodId:
      values.settlement?.search?.endSettlementPeriodId ?? '',
  };
  const settlements = useGetSettlementLabelInfinite(labelId, setlementSearch);
  const tableProps = useInfiniteTable(settlements, { selectable: false });

  const statistics = useGetSettlementLabelStatistics(labelId, setlementSearch);

  const { mutateAsync: deleteSettlement } = useDeleteSettlement();

  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false);

  const settlementPeriods = useGetSettlementLightList({ controlApplicationSettings: true });

  const createSettlementOrder = useCreateSettlementOrder();
  const createSettlementOrderToNewest = useCreateSettlementOrderToNewest();
  const acceptSettlementOrder = useAcceptSettlementOrder();
  const rejectSettlementOrder = useRejectSettlementOrder();

  const settlementOrsers = useGetSettlementOrderQueue(labelId);

  const settlementPeriodOptions = settlementPeriods.data?.flat().map(s => ({
    label: getSettlementPeriodName(s.settlementPeriodName),
    value: s.id,
  }));

  const [showWarningPopup, setShowWarningPopup] = useState(false);

  const checkPeriods = useCheckSettlementPeriods();

  const getWarningMessage = () => {
    const messages = [];

    if (checkPeriods.data?.hasOverlap) {
      messages.push(t('settlement.warning.message.hasOverlap'));
    }

    if (checkPeriods.data?.labelReportOverlap) {
      messages.push(t('settlement.warning.message.labelReportOverlap'));
    }

    if (messages.length > 0) {
      return messages.join('\n\n');
    }

    if (checkPeriods.data?.hasGap) {
      return t('settlement.warning.message.hasGap');
    }

    return '';
  };

  const clearGenerateValues = () => {
    setFieldValue('settlement.generate', {
      startSettlementPeriodId: '',
      endSettlementPeriodId: '',
      planedSettlementDate: formatDateToInput(new Date(), true),
      asap: true,
    });
    setFieldValue('settlement.generateToNewest', {
      dividePeriods: false,
      planedSettlementDate: formatDateToInput(new Date(), true),
      asap: true,
    });
  };

  const onGenerateSettlementToNewest = async (actions: PopupActions) => {
    if (values.settlement?.generateToNewest) {
      await createSettlementOrderToNewest.mutateAsync(
        mapCreateSettlementOrderToNewestBody(
          labelId,
          values.settlement.generateToNewest
        ),
        {
          onSuccess: async data => {
            setShowWarningPopup(false);
            await settlements.refetch();
            await settlementTimeline.refetch();
            await settlementOrsers.refetch();

            actions.close();

            clearGenerateValues();
          },
          onError: error => onErrorShowAllErrorsToast(error),
        }
      );
    }
  };
  const onGenerateSettlement = async (actions: PopupActions) => {
    if (values.settlement?.generate) {
      await createSettlementOrder.mutateAsync(
        mapCreateSettlementOrderBody({
          labelId,
          ...values.settlement.generate,
        }),
        {
          onSuccess: async data => {
            setShowWarningPopup(false);
            await settlements.refetch();
            await settlementTimeline.refetch();
            await settlementOrsers.refetch();

            actions.close();

            clearGenerateValues();
          },
          onError: error => onErrorShowAllErrorsToast(error),
        }
      );
    }
  };

  const onCheckSettlement = async (actions: PopupActions) => {
    if (values.settlement?.generate) {
      const checkResult = await checkPeriods.mutateAsync({
        labelId,
        startSettlementPeriodId:
          values.settlement?.generate?.startSettlementPeriodId,
        endSettlementPeriodId:
          values.settlement?.generate?.endSettlementPeriodId,
      });

      if (
        checkResult.hasOverlap ||
        checkResult.labelReportOverlap ||
        checkResult.hasGap
      ) {
        setShowWarningPopup(true);
        actions.close();
        return;
      }

      await onGenerateSettlement(actions);
    }
  };

  const netAmountLength = formatCurrency(statistics?.data?.allNetAmount).length;

  useEffect(() => {
    settlements.refetch();
  }, [settlementOrsers.data]);

  const handleDelete = async (
    settlementId: string,
    actions: PopupActions | null
  ) => {
    await deleteSettlement(
      { settlementId },
      {
        onSuccess: () => {
          settlements.refetch();
          actions?.close();
        },
      }
    );
  };

  const handleAcceptSettlementOrder = async (settlementOrderId: string) => {
    await acceptSettlementOrder.mutateAsync(
      { settlementOrderId },
      {
        onSuccess: async () => {
          await settlementOrsers.refetch();
          await settlements.refetch();
          await settlementTimeline.refetch();
          await queryClient.invalidateQueries({
            queryKey: ['settlement-requests-count'],
          });
        },
        onError: error => onErrorShowAllErrorsToast(error),
      }
    );
  };

  const handleRejectSettlementOrder = async (settlementOrderId: string) => {
    await rejectSettlementOrder.mutateAsync(
      { settlementOrderId },
      {
        onSuccess: async () => {
          await settlementOrsers.refetch();
          await settlements.refetch();
          await settlementTimeline.refetch();
          await queryClient.invalidateQueries({
            queryKey: ['settlement-requests-count'],
          });
        },
        onError: error => onErrorShowAllErrorsToast(error),
      }
    );
  };

  return (
    <TopbarPage
      topbar={
        <TopBar
          size='compact'
          useHelmet={false}
          hideHeader
          isChildrenVisible={true}
          customBackItem={<div className='none' />}
          topbarContent={
            <div className='flex w-full justify-between gap-4'>
              <div className='flex gap-2 w-[500px]'>
                <FormInput.Select
                  name='settlement.search.startSettlementPeriodId'
                  label={t('input.label.startSettlementPeriod')}
                  options={settlementPeriodOptions}
                  placeholder={''}
                  displayMode={false}
                />
                <FormInput.Select
                  name='settlement.search.endSettlementPeriodId'
                  label={t('input.label.endSettlementPeriod')}
                  options={settlementPeriodOptions}
                  placeholder={''}
                  displayMode={false}
                />
              </div>
              {hasPermission('SettlementPermissionsLIST**', 1) && (
                <Button
                  variant={BUTTON_VARIANT.submit}
                  onClick={handleComponentVisible}
                  outsideChildren={
                    <DropdownMenu
                      ref={ref}
                      visible={isComponentVisible}
                      style={{ padding: 0 }}
                      justifyPosition='end'>
                      <MagellanPopups.PopupChannelCreateForm
                        onAdd={onCheckSettlement}
                        disabledOnAdd={
                          !values.settlement?.generate?.endSettlementPeriodId ||
                          !values.settlement?.generate?.startSettlementPeriodId
                        }
                        clearPopupValues={clearGenerateValues}
                        trigger={
                          <Button isClear fullWidth>
                            <Button.Text text={t('button.in-periods')} />
                          </Button>
                        }
                        header={t('settlement-order.create.header')}>
                        <FormInput.Select
                          label={t('input.label.startSettlementPeriod')}
                          name='settlement.generate.startSettlementPeriodId'
                          isAsync
                          options={settlementPeriodOptions}
                          displayMode={false}
                        />
                        <FormInput.Select
                          label={t('input.label.endSettlementPeriod')}
                          name='settlement.generate.endSettlementPeriodId'
                          isAsync
                          options={settlementPeriodOptions}
                          displayMode={false}
                        />
                        <FormInput.Select
                          displayMode={false}
                          name={`settlement.generate.asap`}
                          label={t('input.label.asap')}
                          isClearable={false}
                          options={[
                            { label: t('input.option.yes'), value: true },
                            { label: t('input.option.no'), value: false },
                          ]}
                        />
                        <FormInput.Text
                          label={t('input.label.plannedStartDate')}
                          type='datetime-local'
                          min={formatDateToInput(new Date(), true)}
                          name='settlement.generate.planedSettlementDate'
                          disabled={values.settlement?.generate?.asap}
                          displayMode={false}
                        />
                      </MagellanPopups.PopupChannelCreateForm>
                      <MagellanPopups.PopupChannelCreateForm
                        onAdd={onGenerateSettlementToNewest}
                        clearPopupValues={clearGenerateValues}
                        trigger={
                          <Button isClear fullWidth>
                            <Button.Text text={t('button.to-newest')} />
                          </Button>
                        }
                        header={t('settlement-order.create.headerToNewest')}>
                        <FormInput.Select
                          displayMode={false}
                          name={`settlement.generateToNewest.dividePeriods`}
                          label={t('input.label.dividePeriods')}
                          isClearable={false}
                          options={[
                            { label: t('input.option.yes'), value: true },
                            { label: t('input.option.no'), value: false },
                          ]}
                        />
                        <FormInput.Select
                          displayMode={false}
                          isClearable={false}
                          name={`settlement.generateToNewest.asap`}
                          label={t('input.label.asap')}
                          options={[
                            { label: t('input.option.yes'), value: true },
                            { label: t('input.option.no'), value: false },
                          ]}
                        />
                        <FormInput.Text
                          label={t('input.label.plannedStartDate')}
                          type='datetime-local'
                          min={formatDateToInput(new Date(), true)}
                          name='settlement.generateToNewest.planedSettlementDate'
                          disabled={values.settlement?.generateToNewest?.asap}
                          displayMode={false}
                        />
                      </MagellanPopups.PopupChannelCreateForm>
                    </DropdownMenu>
                  }>
                  <Button.Text text={t('button.generate')} />
                </Button>
              )}
            </div>
          }>
          {!!sortedTimeline?.length && (
            <div>
              <Timeline
                events={sortedTimeline}
                name={item =>
                  getSettlementPeriodName({
                    name: item.settlementPeriodName,
                    year: item.settlementPeriodYear,
                  })
                }
                isEventSelected={item => item.isSettled}
              />
            </div>
          )}
        </TopBar>
      }>
      <div className='flex flex-col w-full gap-4 h-full justify-between'>
        {!!settlementOrsers.data?.length && (
          <div className='flex w-full max-h-[30%]'>
            <Table
              id={'LabelSettlements-order2'}
              data={{ nodes: settlementOrsers.data ?? [] }}
              size='compact'
              columns={[
                {
                  label: t('input.label.label'),
                  renderCell: item => (
                    <TableItem>
                      <TableItem.Text text={item.labelName} />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t('input.label.startSettlementPeriod'),
                  renderCell: item => (
                    <TableItem>
                      {item.startSettlementPeriodName?.name && (
                        <TableItem.Text
                          text={getSettlementPeriodName(
                            item.startSettlementPeriodName
                          )}
                        />
                      )}
                    </TableItem>
                  ),
                  resize: true,
                },

                {
                  label: t('input.label.endSettlementPeriod'),
                  renderCell: item => (
                    <TableItem>
                      {item.endSettlementPeriodName?.name && (
                        <TableItem.Text
                          text={getSettlementPeriodName(
                            item.endSettlementPeriodName
                          )}
                        />
                      )}
                    </TableItem>
                  ),
                  resize: true,
                },

                {
                  label: t('input.label.date'),
                  renderCell: item => (
                    <TableItem>
                      <TableItem.Text
                        text={formatDate(item.settlementDate, true)}
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t('input.label.status'),
                  renderCell: item => (
                    <TableItem>
                      <LabeledSettlementOrderStatus
                        status={item.settlementOrderStatusId}
                      />
                    </TableItem>
                  ),
                },
                {
                  label: '',
                  renderCell: item => (
                    <TableItem justifyPosition='end'>
                      {item.settlementOrderStatusId ===
                        SETTLEMENT_ORDER_STATUS.request && (
                        <div className='flex gap-2'>
                          <Button
                            isClear
                            onlyIcon
                            variant={BUTTON_VARIANT.submit}
                            disabled={
                              (acceptSettlementOrder.isPending &&
                                acceptSettlementOrder.variables
                                  ?.settlementOrderId === item.id) ||
                              (rejectSettlementOrder.isPending &&
                                rejectSettlementOrder.variables
                                  ?.settlementOrderId === item.id)
                            }
                            isLoading={
                              acceptSettlementOrder.isPending &&
                              acceptSettlementOrder.variables
                                ?.settlementOrderId === item.id
                            }
                            onClick={() =>
                              handleAcceptSettlementOrder(item.id)
                            }>
                                                        <Button.Description description={t('button.accept')} />

                                                        <Button.Icon icon={<PiCheck />} />
                          </Button>
                          <Button
                            isClear
                            onlyIcon
                            disabled={
                              (rejectSettlementOrder.isPending &&
                                rejectSettlementOrder.variables
                                  ?.settlementOrderId === item.id) ||
                              (acceptSettlementOrder.isPending &&
                                acceptSettlementOrder.variables
                                  ?.settlementOrderId === item.id)
                            }
                            isLoading={
                              rejectSettlementOrder.isPending &&
                              rejectSettlementOrder.variables
                                ?.settlementOrderId === item.id
                            }
                            onClick={() =>
                              handleRejectSettlementOrder(item.id)
                            }>
                                                        <Button.Description description={t('button.reject')} />

                                                        <Button.Icon icon={<PiX />} />
                          </Button>
                        </div>
                      )}
                    </TableItem>
                  ),
                },
              ]}
            />
          </div>
        )}
        <div
          className={`flex w-full ${
            !!settlementOrsers.data?.length ? 'h-[70%]' : 'h-full'
          }`}>
          <FooterTable
            id={columnsVisibility.tableId}
            size='compact'
            {...tableProps}
            columns={[
              {
                label: t('input.label.startSettlementPeriod'),
                renderCell: ({ startSettlementPeriodName }) => (
                  <TableItem>
                    <TableItem.Text
                      text={getSettlementPeriodName(startSettlementPeriodName)}
                    />
                  </TableItem>
                ),
                resize: true,
                hide: columnsVisibility.hide(SORT_COLUMNS.year),
                footer: t('table.total'),
              },
              {
                label: t('input.label.endSettlementPeriod'),
                renderCell: ({ endSettlementPeriodName }) => (
                  <TableItem>
                    <TableItem.Text
                      text={getSettlementPeriodName(endSettlementPeriodName)}
                    />
                  </TableItem>
                ),
                resize: true,
                hide: columnsVisibility.hide(SORT_COLUMNS.year),
              },
              {
                label: t('input.label.settlementNumber'),
                renderCell: ({ settlementNumber }) => (
                  <TableItem>
                    <TableItem.Text text={settlementNumber} />
                  </TableItem>
                ),
                resize: true,
                hide: columnsVisibility.hide(SORT_COLUMNS.settlementNumber),
              },
              {
                label: t('input.label.net'),
                renderCell: ({ netAmount }) => (
                  <TableItem>
                    <TableItem.Currency
                      text={formatCurrency(netAmount, {
                        length: netAmountLength,
                      })}
                    />
                  </TableItem>
                ),
                resize: true,
                hide: columnsVisibility.hide(SORT_COLUMNS.net),
                footer: formatCurrency(statistics.data?.allNetAmount, {
                  length: netAmountLength,
                }),
              },
              {
                label: t('input.label.email-sender'),
                renderCell: ({ id, sendStatus }) => (
                  <TableItem>
                    <div className='flex items-center gap-2'>
                      {sendStatus !== EMAIL_SEND.notSend && (
                        <LabeledEmailSend status={sendStatus} />
                      )}
                      {sendStatus !== EMAIL_SEND.planned && (
                        <SendButtonEmail
                          documentId={id}
                          documentType={DOCUMENT_TYPE.settlement}
                          refetch={settlements.refetch}
                          status={sendStatus}
                        />
                      )}
                    </div>
                  </TableItem>
                ),
                resize: true,
              },
              {
                label: '',
                renderCell: ({ filePath, id, settlementNumber, labelName }) => (
                  <TableItem justifyPosition='end'>
                    <div className='flex'>
                      <MagellanPopups.FilePreviewPopup
                        fileId={id}
                        fileName={settlementNumber ?? ''}
                        fileService={useGetSettlementFilePreview}
                        trigger={
                          <Button
                            type='button'
                            isClear
                            disabled={!filePath}
                            onlyIcon>
                                                        <Button.Description description={t('button.download')} />

                                                        <Button.Icon icon={<PiDownload />} />
                          </Button>
                        }
                        onDownload={() => setFile({ id, name: settlementNumber })}
                      />
                      <MagellanPopups.DeleteWarning
                        name={`${t('settlement.what')} ${settlementNumber} ${
                          labelName ?? ''
                        } `}
                        onRemove={actions => handleDelete(id, actions)}
                      />
                    </div>
                  </TableItem>
                ),
                resize: true,
              },
            ]}
          />
        </div>
      </div>
      <MagellanPopups.WarningPopup
        open={showWarningPopup}
        onClose={() => setShowWarningPopup(false)}
        onConfirm={
          checkPeriods.data?.hasOverlap || checkPeriods.data?.labelReportOverlap
            ? undefined
            : onGenerateSettlement
        }
        title={t('settlement.warning.title')}
        message={getWarningMessage()}
      />
    </TopbarPage>
  );
};

export default Settlements;
